import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useEnhancedImage } from '@/hooks/useEnhancedImage';
import { cn } from '@/lib/utils';
import { Sparkles } from 'lucide-react';
import { getOptimizedImageUrl } from '@/utils/imageOptimization';

interface EnhancedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  enableEnhancement?: boolean;
  optimizedWidth?: number;
  fallbackSrc?: string;
}

export const EnhancedImage: React.FC<EnhancedImageProps> = ({
  src,
  enableEnhancement = true,
  optimizedWidth,
  fallbackSrc,
  className,
  alt,
  loading: loadingProp,
  sizes: sizesProp,
  onError,
  ...props
}) => {
  const [hasErrored, setHasErrored] = useState(false);
  const [optimizedFailed, setOptimizedFailed] = useState(false);
  const effectiveSrc = hasErrored && fallbackSrc ? fallbackSrc : src;
  const { displayUrl, isEnhancing, isEnhanced } = useEnhancedImage(
    enableEnhancement && !hasErrored ? effectiveSrc : null
  );

  const baseSrc = enableEnhancement && !hasErrored ? displayUrl : effectiveSrc;
  // Apply Supabase image transform for optimized delivery
  const optimizedSrc = optimizedWidth && !optimizedFailed
    ? getOptimizedImageUrl(baseSrc, optimizedWidth)
    : baseSrc;

  // Generate srcSet for responsive images when using Supabase optimization
  const srcSet = optimizedWidth && !optimizedFailed
    ? [
        Math.round(optimizedWidth * 0.5),
        optimizedWidth,
        Math.round(optimizedWidth * 1.5),
      ]
        .map((w) => {
          const url = getOptimizedImageUrl(baseSrc, w);
          return url !== baseSrc ? `${url} ${w}w` : null;
        })
        .filter(Boolean)
        .join(', ') || undefined
    : undefined;

  const imgRef = useRef<HTMLImageElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setHasErrored(false);
    setOptimizedFailed(false);
    setIsLoaded(false);
  }, [src, fallbackSrc]);

  const handleLoad = useCallback(() => setIsLoaded(true), []);
  const handleError = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    if (!optimizedFailed && optimizedSrc !== baseSrc) {
      setOptimizedFailed(true);
    } else if (!hasErrored && fallbackSrc && effectiveSrc !== fallbackSrc) {
      setHasErrored(true);
      setOptimizedFailed(false);
    } else {
      setIsLoaded(true); // stop the skeleton spinner even if no fallback
    }
    onError?.(e);
  }, [baseSrc, effectiveSrc, fallbackSrc, hasErrored, onError, optimizedFailed, optimizedSrc]);

  const nativeLoading = loadingProp ?? 'lazy';
  const defaultSizes = '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw';

  return (
    <div className="relative w-full h-full">
      {!isLoaded && (
        <div className="absolute inset-0 bg-muted animate-pulse rounded" />
      )}
      <img
        ref={imgRef}
        src={optimizedSrc}
        srcSet={srcSet}
        alt={alt}
        loading={nativeLoading}
        decoding="async"
        sizes={sizesProp || defaultSizes}
        onLoad={handleLoad}
        onError={handleError}
        className={cn(
          'transition-opacity duration-300',
          isEnhancing && 'opacity-90',
          !isLoaded && 'opacity-0',
          isLoaded && 'opacity-100',
          className
        )}
        {...props}
      />
      {isEnhancing && (
        <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1 animate-pulse">
          <Sparkles className="h-3 w-3" />
          Enhancing...
        </div>
      )}
      {isEnhanced && !isEnhancing && (
        <div className="absolute bottom-2 left-2 bg-primary/80 text-primary-foreground text-xs px-2 py-1 rounded-full flex items-center gap-1">
          <Sparkles className="h-3 w-3" />
          HD
        </div>
      )}
    </div>
  );
};
